import { KeyOutlined, MobileOutlined, UserAddOutlined } from "@ant-design/icons";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { App, Button, Card, Flex, Form, Input, Modal, Select, Space, Table, Tag, Typography } from "antd";
import { useState } from "react";
import { api, unwrap, type Invitation, type Member, type Role } from "@/api/client";
import { useApplyFieldErrors, useErrorText } from "@/api/errors";
import { useAuth } from "@/auth/AuthProvider";
import { useI18n } from "@/i18n";
import { CLIENT_PAGING } from "@/components/list/useListPage";
import { useWarehouses } from "@/lib/useWarehouses";
import { PinModal, StaffModal } from "./Pins";

const ROLES: Role[] = ["owner", "warehouse_manager", "warehouse_staff", "finance_cs"];

/** Pengaturan: User & Role (App Flow §10). Owner only. */
export function UserSettings() {
  const { t, formatDateTime, formatDate } = useI18n();
  const { me } = useAuth();
  const { message, modal } = App.useApp();
  const queryClient = useQueryClient();
  const errorText = useErrorText();
  const applyFieldErrors = useApplyFieldErrors();
  const [inviteOpen, setInviteOpen] = useState(false);
  const [pinOf, setPinOf] = useState<Member | null>(null);
  const [staffOpen, setStaffOpen] = useState(false);
  const [form] = Form.useForm<{ email: string; role: Role }>();
  const tz = me?.workspace?.timezone;

  const data = useQuery({ queryKey: ["members"], queryFn: () => unwrap(api.GET("/v1/members")) });
  const refresh = () => queryClient.invalidateQueries({ queryKey: ["members"] });
  const roleOptions = ROLES.map((r) => ({ value: r, label: t(`role.${r}`) }));

  const invite = useMutation({
    mutationFn: (body: { email: string; role: Role }) => unwrap(api.POST("/v1/invitations", { body })),
    onSuccess: (inv) => {
      setInviteOpen(false);
      void message.success(t("settings.users.invited", { email: inv.email }));
      void refresh();
    },
    onError: (err) => {
      if (!applyFieldErrors(form, err)) void message.error(errorText(err));
    },
  });

  const warehouses = useWarehouses();
  const update = async (m: Member, body: { role?: Role; status?: "active" | "disabled"; allWarehouses?: boolean; warehouseIds?: string[] }) => {
    try {
      await unwrap(api.PATCH("/v1/members/{userID}", { params: { path: { userID: m.userId } }, body }));
      if (body.role) void message.success(t("settings.users.roleChanged", { name: m.name }));
      if (body.allWarehouses !== undefined) void message.success(t("settings.users.warehousesChanged", { name: m.name }));
      await refresh();
    } catch (err) {
      void message.error(errorText(err));
      await refresh();
    }
  };

  const revoke = (inv: Invitation) =>
    modal.confirm({
      title: t("settings.users.revoke"),
      content: t("settings.users.revokeConfirm", { email: inv.email }),
      okText: t("settings.users.revoke"),
      okButtonProps: { danger: true },
      cancelText: t("common.cancel"),
      onOk: () =>
        unwrap(api.DELETE("/v1/invitations/{invitationID}", { params: { path: { invitationID: inv.id } } }))
          .then(refresh)
          .catch((err: unknown) => void message.error(errorText(err))),
    });

  return (
    <>
      <Flex justify="space-between" align="center" style={{ marginBottom: 16 }}>
        <Typography.Title level={2} style={{ margin: 0, fontSize: 28 }}>
          {t("settings.users.title")}
        </Typography.Title>
        <Space wrap>
          <Button icon={<MobileOutlined />} onClick={() => setStaffOpen(true)}>
            {t("pin.staffAdd")}
          </Button>
          <Button
            type="primary"
            icon={<UserAddOutlined />}
            onClick={() => {
              form.resetFields();
              setInviteOpen(true);
            }}
          >
            {t("settings.users.invite")}
          </Button>
        </Space>
      </Flex>

      <Table<Member>
        rowKey="userId"
        loading={data.isPending}
        dataSource={data.data?.members}
        pagination={CLIENT_PAGING}
        columns={[
          {
            title: t("common.name"),
            dataIndex: "name",
            render: (name: string, m) => (
              <Space orientation="vertical" size={0}>
                <span>
                  {name} {m.userId === me?.user.id && <Tag>{t("settings.users.you")}</Tag>}
                </span>
                <Typography.Text type="secondary">{m.email || t("pin.noEmail")}</Typography.Text>
              </Space>
            ),
          },
          {
            title: t("settings.users.role"),
            dataIndex: "role",
            width: 260,
            render: (role: Role, m) => (
              <Select<Role>
                value={role}
                options={roleOptions}
                style={{ width: "100%" }}
                disabled={m.status !== "active"}
                onChange={(next) => void update(m, { role: next })}
                aria-label={t("settings.users.role")}
              />
            ),
          },
          {
            title: t("settings.users.warehouses"),
            dataIndex: "warehouseIds",
            width: 300,
            // "*": every warehouse. Owners always have every warehouse.
            render: (ids: string[] | null, m) => (
              <Select<string[]>
                mode="multiple"
                value={m.role === "owner" || ids === null ? ["*"] : ids}
                disabled={m.role === "owner" || m.status !== "active"}
                style={{ width: "100%" }}
                maxTagCount="responsive"
                aria-label={t("settings.users.warehouses")}
                options={[
                  { value: "*", label: t("settings.users.allWarehouses") },
                  ...warehouses.active.map((w) => ({ value: w.id, label: w.name })),
                ]}
                onChange={(next) => {
                  const added = next.find((v) => !(ids ?? ["*"]).includes(v));
                  if (added === "*" || next.length === 0) return void update(m, { allWarehouses: true });
                  void update(m, { allWarehouses: false, warehouseIds: next.filter((v) => v !== "*") });
                }}
              />
            ),
          },
          {
            title: t("pin.column"),
            dataIndex: "loginId",
            width: 170,
            render: (id: string | null, m) => (
              <Space size={4}>
                {id && m.hasPin ? <Tag className="z-num">{id}</Tag> : <Typography.Text type="secondary">—</Typography.Text>}
                {m.status === "active" && (m.role !== "finance_cs" || m.hasPin) && (
                  <Button size="small" type="text" icon={<KeyOutlined />} aria-label={t("pin.set")} title={t("pin.set")} onClick={() => setPinOf(m)} />
                )}
                {m.hasPin && (
                  <Button
                    size="small"
                    type="link"
                    danger
                    onClick={() =>
                      modal.confirm({
                        title: t("pin.clear"),
                        content: t("pin.clearConfirm", { name: m.name }),
                        okText: t("pin.clear"),
                        okButtonProps: { danger: true },
                        cancelText: t("common.cancel"),
                        onOk: () =>
                          unwrap(api.DELETE("/v1/members/{userID}/pin", { params: { path: { userID: m.userId } } }))
                            .then(refresh)
                            .catch((err: unknown) => void message.error(errorText(err))),
                      })
                    }
                  >
                    {t("pin.clear")}
                  </Button>
                )}
              </Space>
            ),
          },
          {
            title: t("common.status"),
            dataIndex: "status",
            width: 120,
            render: (s: Member["status"]) =>
              s === "active" ? <Tag color="success">{t("settings.users.active")}</Tag> : <Tag>{t("settings.users.disabled")}</Tag>,
          },
          {
            title: t("settings.users.lastLogin"),
            dataIndex: "lastLoginAt",
            width: 200,
            render: (v: string | null) => (v ? formatDateTime(v, tz) : t("common.never")),
          },
          {
            title: t("common.actions"),
            width: 160,
            render: (_, m) =>
              m.userId === me?.user.id ? null : m.status === "active" ? (
                <Button
                  size="small"
                  danger
                  onClick={() =>
                    modal.confirm({
                      title: t("settings.users.deactivate"),
                      content: t("settings.users.deactivateConfirm", { name: m.name }),
                      okText: t("settings.users.deactivate"),
                      okButtonProps: { danger: true },
                      cancelText: t("common.cancel"),
                      onOk: () => update(m, { status: "disabled" }),
                    })
                  }
                >
                  {t("settings.users.deactivate")}
                </Button>
              ) : (
                <Button size="small" onClick={() => void update(m, { status: "active" })}>
                  {t("settings.users.activate")}
                </Button>
              ),
          },
        ]}
      />

      {(data.data?.invitations.length ?? 0) > 0 && (
        <Card title={t("settings.users.pending")} style={{ marginTop: 24 }}>
          <Table<Invitation>
            rowKey="id"
            size="small"
            dataSource={data.data?.invitations}
            pagination={false}
            columns={[
              { title: t("common.email"), dataIndex: "email" },
              { title: t("settings.users.role"), dataIndex: "role", render: (r: Role) => t(`role.${r}`) },
              { title: t("settings.users.expires"), dataIndex: "expiresAt", render: (v: string) => formatDate(v, tz) },
              {
                title: t("common.actions"),
                width: 140,
                render: (_, inv) => (
                  <Button size="small" danger onClick={() => revoke(inv)}>
                    {t("settings.users.revoke")}
                  </Button>
                ),
              },
            ]}
          />
        </Card>
      )}

      <PinModal member={pinOf} onClose={() => setPinOf(null)} />
      <StaffModal open={staffOpen} onClose={() => setStaffOpen(false)} />
      <Modal
        open={inviteOpen}
        title={t("settings.users.invite")}
        okText={t("settings.users.inviteSubmit")}
        cancelText={t("common.cancel")}
        confirmLoading={invite.isPending}
        onOk={() => form.submit()}
        onCancel={() => setInviteOpen(false)}
        destroyOnHidden
      >
        <Form form={form} layout="vertical" requiredMark={false} initialValues={{ role: "warehouse_staff" }} onFinish={(v) => invite.mutate(v)}>
          <Form.Item name="email" label={t("common.email")} rules={[{ required: true, type: "email", message: t("field.invalid_email") }]}>
            <Input autoFocus inputMode="email" />
          </Form.Item>
          <Form.Item name="role" label={t("settings.users.role")} rules={[{ required: true }]}>
            <Select options={roleOptions} />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}
